import Note from "../../models/soundslice/Note";
import {emitSheetMusicNotesCurrentlyBeingPlayed_changed} from "../eventBus/notes/notesEventBus";

/**
 * setTimeout becomes inaccurate when under intense cpu load.  To get around this we need to move setTimeouts for note on and note off into a separate worker thread.
 * When scheduleNoteEvents is called, this class will start emitting sheetMusicNotesCurrentlyBeingPlayedChanged rxjs events, so that fretboard, scoring, etc can react.
 */
class NotesEventWorker{
  worker: Worker;
  constructor(){
    this.worker =  new Worker(`/workers/overhead-keyboard-notes-worker.js`);
    this.worker.onmessage = (e) => this.handleMessageFromWorker(e);
  }

  /**
   * When play button is pressed, we schedule audio playback using the webAudio api.
   * That api does not have a mechanism to determine when notes are being played, so we must do separate/redundant scheduling to do things like score playback (piano) or display on the fretboard what notes are being played.
   * @param notes - notes to schedule
   * @param speed - note offset and duration is not relative to speed, so we need to perform relative calculations.
   * @param seekOffsetSeconds - note offset is not relative to where the cursor/seek location is, so we need to perform relative calculations.
   * @param loopEndOffsetSeconds - to avoid scheduling notes from the bar after the loop end marker, we can filter notes out that come after this value.
   */
  scheduleNoteEvents(notes: Note[], speed: number, seekOffsetSeconds: number, loopEndOffsetSeconds: number){
    emitSheetMusicNotesCurrentlyBeingPlayed_changed([]);//reset
    //avoid unnecessarily scheduling of notes by filtering notes with offsets between seekOffsetSeconds and loopEndOffsetSeconds
    const notesToSchedule = getNotesBetweenOffsets(notes, seekOffsetSeconds, loopEndOffsetSeconds);
    this.worker.postMessage({method: 'scheduleNoteEvents', notes: notesToSchedule, speed, seekOffsetSeconds});
  }

  /**
   * When stop button is pressed, we not to cancel any scheduled setTimeouts/
   */
  cancelScheduledNoteEvents(){
    this.worker.postMessage({method: `cancelScheduledNoteEvents`});
  }

  /**
   * Handle messages from our worker thread.
   * @param e - message event from worker
   */
  handleMessageFromWorker(e: MessageEvent){
    const method = e.data.method;
    switch(method){
      case `notesCurrentlyBeingPlayedChanged`:
        const {notesCurrentlyBeingPlayed} = e.data;
        this.handleNotesCurrentlyBeingPlayedChangedEventFromWorker(notesCurrentlyBeingPlayed);
        break;
    }
  }

  /**
   * When the worker tells us that the notes currently being played has changed, we need to emit an rxjs event so things like fretboard, piano, etc can subscribe.
   * @param notesCurrentlyBeingPlayed - array of notes that are currently being played.
   */
  handleNotesCurrentlyBeingPlayedChangedEventFromWorker(notesCurrentlyBeingPlayed: Note[]){
    emitSheetMusicNotesCurrentlyBeingPlayed_changed(notesCurrentlyBeingPlayed);
  }
}

/**
 * Find and return notes after the specified seekOffsetSeconds and before loopEndOffsetSeconds
 * @param notes - array of notes to be evaluated
 * @param seekOffsetSeconds - where the cursor seek position is, in seconds.
 * @param loopEndOffsetSeconds - note offsets should come before this value
 */
function getNotesBetweenOffsets(notes: Note[], seekOffsetSeconds: number, loopEndOffsetSeconds: number){
  loopEndOffsetSeconds = loopEndOffsetSeconds < 0 ? Number.POSITIVE_INFINITY : loopEndOffsetSeconds;
  return notes.filter(n => n.offset >= seekOffsetSeconds && n.offset < loopEndOffsetSeconds);
}

//singleton
const notesEventWorker = new NotesEventWorker();
export default notesEventWorker;
